import React from 'react';
import { Bell, Lightbulb, Play, Square, Sun, Vibrate } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PanelSection } from '../ui/PanelSection';
import { Chip } from '../ui/Chip';
import { EmptyNote } from '../ui/EmptyNote';
import { NumberChoice } from '../ui/NumberChoice';
import { NumberStepper } from '../ui/NumberStepper';
import { SegmentedChoice } from '../ui/SegmentedChoice';
import { useRcuLink } from '../useRcuLink';
import { TONE } from '../../../lib/rcu/catalog/feedback';
import { PANEL_EVENT_TAIL } from '../../../lib/rcu/catalog/link';

import {
  DALI_ADDRESS,
  DALI_ADDRESS_KIND,
  DALI_MASTER,
  DALI_MODES,
  DALI_MODE,
  DALI_MODE_LABEL,
  DALI_PARAM,
  daliBridgeLabel,
  daliModeLabel,
  daliNakLabel,
} from '../../../lib/rcu/catalog/dali';
import type { DaliAddressKind, DaliMode } from '../../../lib/rcu/catalog/dali';
import { consoleChannels, firmwareChannel } from '../../../lib/rcu/catalog/dinValue';
import {
  DIN_VALUE,
  RCU_GROUP,
  MS_PER_SECOND,
  RCU_PANEL_LISTEN_SECONDS,
  RCU_GROUPS,
  RELAY_NUMBERING,
  SECONDS_PER_MINUTE,
  RELAY_VALUE,
  RELAY_VALUE_LABEL,
  dinValueLabel,
  moduleIdOfRail,
  relayValueLabel,
} from '../../../lib/rcu/catalog/protocol';
import { PANEL_STATE } from '../../../lib/rcu/catalog/ack';
import { RAIL_TYPE_SPECS } from '../../../lib/rcu/catalog/railType';
import { listenPanel, operateDali, operateDin, operateRelay, readDali } from '../../../lib/rcu/link/commands';
import { daliKey, dinKey } from '../../../lib/rcu/link/live';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useRcuLinkStore } from '../../../stores/rcuLinkStore';

/** The relay operations a technician actually issues, in the order they use them. */
const RELAY_ACTIONS = [
  RELAY_VALUE.TOGGLE,
  RELAY_VALUE.ON,
  RELAY_VALUE.OFF,
  RELAY_VALUE.BELL,
] as const;

type RelayAction = (typeof RELAY_ACTIONS)[number];

/** A pill's value is a string, so the operations are named by their number. */
const relayActionId = (action: RelayAction): string => String(action);

const relayActionFromId = (id: string): RelayAction =>
  RELAY_ACTIONS.find((action) => relayActionId(action) === id) ?? RELAY_VALUE.TOGGLE;

const RELAY_ACTION_ICON: Record<RelayAction, LucideIcon> = {
  [RELAY_VALUE.BELL]: Bell,
  [RELAY_VALUE.TOGGLE]: Vibrate,
  [RELAY_VALUE.ON]: Sun,
  [RELAY_VALUE.OFF]: Square,
};

const RelayBank: React.FC = () => {
  const live = useRcuLinkStore((state) => state.live);
  const { send, inFlight } = useRcuLink();
  const [group, setGroup] = React.useState<number>(RCU_GROUP.DEFAULT);
  const [action, setAction] = React.useState<RelayAction>(RELAY_VALUE.TOGGLE);

  const relays = Array.from(
    { length: RELAY_NUMBERING.LAST - RELAY_NUMBERING.FIRST + 1 },
    (_unused, index) => RELAY_NUMBERING.FIRST + index,
  );

  return (
    <PanelSection
      title="RCU relays"
      description="Tap a tile to send the chosen operation to that relay."
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <NumberChoice
          label="Group"
          values={RCU_GROUPS}
          value={group}
          onChange={setGroup}
          hint={`Ordinary RCU use means group ${RCU_GROUP.DEFAULT}; the firmware exposes groups ${RCU_GROUP.MIN}–${RCU_GROUP.MAX}.`}
        />
        <SegmentedChoice
          label="Operation"
          value={relayActionId(action)}
          options={RELAY_ACTIONS.map((entry) => ({
            value: relayActionId(entry),
            label: RELAY_VALUE_LABEL[entry],
            icon: RELAY_ACTION_ICON[entry],
          }))}
          onChange={(next) => setAction(relayActionFromId(next))}
        />
      </div>

      <div className="mt-4 grid grid-cols-5 gap-1.5 sm:grid-cols-8 lg:grid-cols-[repeat(13,minmax(0,1fr))]">
        {relays.map((relay) => {
          const value = live.relays[`${group}:${relay}`];
          const isOn = value !== undefined && value !== DIN_VALUE.MIN;
          return (
            <button
              key={relay}
              type="button"
              disabled={inFlight > 0}
              onClick={() =>
                void send(
                  operateRelay({ group, relay, value: action }),
                  `Relay ${relay} ${RELAY_VALUE_LABEL[action]}`,
                )
              }
              className={`flex aspect-square flex-col items-center justify-center gap-0.5 rounded-xl border text-xs font-medium transition-colors disabled:opacity-50 ${
                value === undefined
                  ? 'border-dashed border-[#E0E0E0] text-[#999999] hover:border-[#D4A054]/60'
                  : isOn
                    ? 'border-[#D4A054] bg-[#FDF6E9] text-[#333333]'
                    : 'border-[#E0E0E0] bg-white text-[#666666] hover:border-[#D4A054]/60'
              }`}
            >
              <span className="font-mono text-sm">{relay}</span>
              <span className="text-[10px] text-[#666666]">
                {value === undefined ? '—' : relayValueLabel(value)}
              </span>
            </button>
          );
        })}
      </div>
    </PanelSection>
  );
};

const DinChannels: React.FC = () => {
  const config = useRcuConfigStore((state) => state.config);
  const live = useRcuLinkStore((state) => state.live);
  const { send, inFlight } = useRcuLink();

  if (config.rails.length === 0) {
    return (
      <PanelSection title="DIN channels" description="Channels of the DIN rails this RCU drives.">
        <EmptyNote message="No DIN rails are defined yet. Add them under DIN rails first." />
      </PanelSection>
    );
  }

  return (
    <PanelSection
      title="DIN channels"
      description="Quick actions per channel. Values are read back from the module's own replies."
    >
      <div className="space-y-5">
        {config.rails.map((rail) => {
          const spec = RAIL_TYPE_SPECS[rail.type];
          const moduleId = moduleIdOfRail(rail.type);
          const actions = spec.channelActions;
          const Icon = spec.icon;
          // Shown 1-4 the way the module is labelled; sent counting from 0.
          const channels = consoleChannels(spec.channels);

          return (
            <div key={rail.id} className="rounded-2xl border border-[#E0E0E0] px-3 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-2 text-sm font-medium text-[#333333]">
                  <Icon className="h-4 w-4 text-[#D4A054]" />
                  {spec.label} · address {rail.address}
                </p>
                <button
                  type="button"
                  disabled={inFlight > 0}
                  onClick={() =>
                    void Promise.all(
                      channels.map((channel) =>
                        send(
                          operateDin({
                            group: RCU_GROUP.DEFAULT,
                            module: moduleId,
                            addr: rail.address,
                            channel: firmwareChannel(channel),
                            value: spec.restValue,
                          }),
                          `${spec.shortLabel} ${rail.address}/${channel} ${spec.restLabel}`,
                        ),
                      ),
                    )
                  }
                  className="rounded-full border border-[#E0E0E0] px-3 py-1.5 text-xs font-medium text-[#666666] transition-colors hover:border-[#D4A054] disabled:opacity-50"
                >
                  {spec.restLabel}
                </button>
              </div>

              <div className="mt-3 grid gap-2 lg:grid-cols-2">
                {channels.map((channel) => {
                  const wire = firmwareChannel(channel);
                  const value = live.din[dinKey(moduleId, rail.address, wire)];
                  return (
                    <div
                      key={channel}
                      className="flex flex-wrap items-center gap-2 rounded-xl bg-[#F5F5F5] px-3 py-2"
                    >
                      <span className="w-8 shrink-0 font-mono text-sm text-[#333333]">{channel}</span>
                      <span className="min-w-[4.5rem] shrink-0 text-xs text-[#666666]">
                        {value === undefined ? 'not read' : dinValueLabel(value)}
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {actions.map((entry) => (
                          <button
                            key={entry.label}
                            type="button"
                            disabled={inFlight > 0}
                            onClick={() =>
                              void send(
                                operateDin({
                                  group: RCU_GROUP.DEFAULT,
                                  module: moduleId,
                                  addr: rail.address,
                                  channel: wire,
                                  value: entry.value,
                                }),
                                `${spec.shortLabel} ${rail.address}/${channel} ${entry.label}`,
                              )
                            }
                            className="rounded-full border border-[#E0E0E0] bg-white px-2.5 py-1 text-xs text-[#666666] transition-colors hover:border-[#D4A054] hover:text-[#333333] disabled:opacity-50"
                          >
                            {entry.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </PanelSection>
  );
};

const DALI_MASTERS = Array.from(
  { length: DALI_MASTER.MAX - DALI_MASTER.MIN + 1 },
  (_unused, index) => DALI_MASTER.MIN + index,
);

const DaliCard: React.FC = () => {
  const bridges = useRcuConfigStore((state) => state.config.daliBridges);
  const live = useRcuLinkStore((state) => state.live);
  const { send, inFlight } = useRcuLink();
  const [master, setMaster] = React.useState<number>(0);
  const [kind, setKind] = React.useState<DaliAddressKind>(DALI_ADDRESS_KIND.INDIVIDUAL);
  const [address, setAddress] = React.useState<number>(DALI_ADDRESS.INDIVIDUAL_MIN);
  const [mode, setMode] = React.useState<DaliMode>(DALI_MODE.DIM);
  const [param, setParam] = React.useState<number>(DALI_PARAM.MIN);

  const answer = live.dali[daliKey(master, address)];
  const addressBounds = {
    [DALI_ADDRESS_KIND.INDIVIDUAL]: { min: DALI_ADDRESS.INDIVIDUAL_MIN, max: DALI_ADDRESS.INDIVIDUAL_MAX },
    [DALI_ADDRESS_KIND.GROUP]: { min: DALI_ADDRESS.GROUP_MIN, max: DALI_ADDRESS.GROUP_MAX },
    [DALI_ADDRESS_KIND.BROADCAST]: { min: DALI_ADDRESS.BROADCAST, max: DALI_ADDRESS.BROADCAST },
  }[kind];

  const chooseKind = (next: DaliAddressKind): void => {
    setKind(next);
    setAddress(
      next === DALI_ADDRESS_KIND.GROUP
        ? DALI_ADDRESS.GROUP_MIN
        : next === DALI_ADDRESS_KIND.BROADCAST
          ? DALI_ADDRESS.BROADCAST
          : DALI_ADDRESS.INDIVIDUAL_MIN,
    );
  };

  return (
    <PanelSection
      title="DALI"
      description="Command a bridge, then query the level it reports."
    >
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          {bridges.length === 0 ? (
            <p className="rounded-xl bg-[#F5F5F5] px-3 py-2 text-xs text-[#666666]">
              No DALI bridge is defined on this tablet yet. Add one under DIN rails to name it here;
              any master index still works.
            </p>
          ) : (
            <SegmentedChoice
              label="Configured bridges"
              value={String(master)}
              options={bridges.map((bridge) => ({
                value: String(bridge.master),
                label: daliBridgeLabel(bridge.address, bridge.master),
              }))}
              onChange={(next) => setMaster(Number(next))}
              hint="Defined on this tablet, so the bus you are addressing is never a guess."
            />
          )}
          <NumberChoice
            label="Bridge (master)"
            values={DALI_MASTERS}
            value={master}
            onChange={setMaster}
            hint="The bridge's own index, as the DALI modules on its bus see it."
          />
          <SegmentedChoice
            label="Address kind"
            value={kind}
            options={[
              { value: DALI_ADDRESS_KIND.INDIVIDUAL, label: 'Individual', description: `0–${DALI_ADDRESS.INDIVIDUAL_MAX}` },
              { value: DALI_ADDRESS_KIND.GROUP, label: 'Group', description: `${DALI_ADDRESS.GROUP_MIN}–${DALI_ADDRESS.GROUP_MAX}` },
              { value: DALI_ADDRESS_KIND.BROADCAST, label: 'Broadcast', description: `${DALI_ADDRESS.BROADCAST}` },
            ]}
            onChange={chooseKind}
          />
          <NumberStepper
            label="DALI address"
            value={address}
            min={addressBounds.min}
            max={addressBounds.max}
            onChange={setAddress}
          />
          <SegmentedChoice
            label="Mode"
            value={String(mode)}
            options={DALI_MODES.map((entry) => ({
              value: String(entry),
              label: daliModeLabel(entry),
              description:
                entry === DALI_MODE.QUERY
                  ? 'Ask for the current level; the parameter is forced to 0.'
                  : undefined,
            }))}
            onChange={(next) => setMode(Number(next) as DaliMode)}
          />
          <NumberStepper
            label={mode === DALI_MODE.QUERY ? 'Parameter (unused for a query)' : 'Parameter'}
            value={param}
            min={DALI_PARAM.MIN}
            max={DALI_PARAM.MAX}
            onChange={setParam}
            suffix={mode === DALI_MODE.DIM ? '(level 0–254, 255 = stop fade)' : undefined}
          />
        </div>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={inFlight > 0}
              onClick={() =>
                void send(
                  operateDali({ master, address, mode, param }),
                  `DALI ${master}/${address} ${daliModeLabel(mode)}`,
                )
              }
              className="flex items-center gap-2 rounded-full bg-[#1A1A1A] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#333333] disabled:opacity-50"
            >
              <Play className="h-4 w-4" />
              Send
            </button>
            <button
              type="button"
              disabled={inFlight > 0}
              onClick={() =>
                void send(
                  readDali({ master, address }),
                  `DALI ${master}/${address} query`,
                )
              }
              className="flex items-center gap-2 rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#D4A054] hover:text-[#333333] disabled:opacity-50"
            >
              <Lightbulb className="h-4 w-4" />
              Query level
            </button>
          </div>

          <div className="rounded-2xl bg-[#F5F5F5] px-4 py-3">
            <p className="text-sm font-medium text-[#333333]">Last answer</p>
            {answer === undefined ? (
              <p className="mt-1 text-sm text-[#666666]">
                Nothing heard for master <span className="font-mono">{master}</span>, address{' '}
                <span className="font-mono">{address}</span>.
              </p>
            ) : (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Chip
                  label={answer.success ? 'answered' : 'no answer'}
                  tone={answer.success ? TONE.SUCCESS : TONE.ERROR}
                />
                <span className="text-sm text-[#666666]">
                  {DALI_MODE_LABEL[answer.mode]} ·{' '}
                  {answer.success ? `value ${answer.value}` : daliNakLabel(answer.value) || `NAK ${answer.value}`}
                </span>
              </div>
            )}
            <p className="mt-2 text-xs text-[#666666]">
              A bridge answers its own command first, then the bus answer follows. Both appear in the
              event feed.
            </p>
          </div>
        </div>
      </div>
    </PanelSection>
  );
};

const PanelWatch: React.FC = () => {
  const events = useRcuLinkStore((state) => state.events);
  const live = useRcuLinkStore((state) => state.live);
  const { send, inFlight } = useRcuLink();

  const panelEvents = events
    .filter((event) => event.direction === 'in' && event.reading.kind === 'panel')
    .slice(-PANEL_EVENT_TAIL)
    .reverse();

  /**
   * The window is a fixed length the firmware owns, so the panel shows when the
   * watch started and when the device stops listening, rather than a ticking
   * clock the console would have to invent.
   */
  const startedAt = live.panelStartedAt === null ? null : new Date(live.panelStartedAt);
  const endsAt =
    startedAt === null
      ? null
      : new Date(startedAt.getTime() + RCU_PANEL_LISTEN_SECONDS * MS_PER_SECOND);

  return (
    <PanelSection
      title="Panel watch"
      description={`Watches the service panel for ${RCU_PANEL_LISTEN_SECONDS / SECONDS_PER_MINUTE} minutes and reports every press it sees.`}
      action={
        <button
          type="button"
          disabled={inFlight > 0}
          onClick={() => void send(listenPanel(), 'Panel watch')}
          className="flex shrink-0 items-center gap-2 rounded-full bg-[#1A1A1A] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#333333] disabled:opacity-50"
        >
          <Play className="h-4 w-4" />
          Start watch
        </button>
      }
    >
      {live.panel === null ? (
        <EmptyNote message="No watch running. Start one and press a scene button to see what the RCU reports." />
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Chip
              label={live.panel.state}
              tone={
                live.panel.state === PANEL_STATE.OPERATION
                  ? TONE.INFO
                  : live.panel.state === PANEL_STATE.TIMEOUT
                    ? TONE.NEUTRAL
                    : TONE.SUCCESS
              }
            />
            {startedAt !== null && endsAt !== null && (
              <span className="text-sm text-[#666666]">
                Started {startedAt.toLocaleTimeString()} · the device stops listening at{' '}
                {endsAt.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      )}

      {panelEvents.length > 0 && (
        <ul className="mt-4 space-y-1">
          {panelEvents.map((event) => (
            <li
              key={event.id}
              className="flex items-center justify-between gap-3 rounded-xl bg-[#F5F5F5] px-3 py-2 text-sm"
            >
              <span className="text-[#333333]">{event.summary}</span>
              <span className="shrink-0 font-mono text-xs text-[#666666]">
                {new Date(event.at).toLocaleTimeString()}
              </span>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 text-xs text-[#666666]">
        A press that replaces a scene already running on group {RCU_GROUP.DEFAULT} produces no event,
        and the watch window is fixed in the firmware.
      </p>
    </PanelSection>
  );
};

/**
 * Every command the console can send to an output: the relay bank, each DIN
 * channel, DALI, and the panel watch. Readings come from the live state, so a
 * tile shows what the device last reported rather than what was asked of it.
 */
export const CommandsPanel: React.FC = () => (
  <div className="space-y-5">
    <RelayBank />
    <DinChannels />
    <DaliCard />
    <PanelWatch />
  </div>
);

export default CommandsPanel;
